import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import pako from 'pako';
import { Container, Button, Snackbar, Alert, debounce } from "@mui/material";
import { socket, connectSocket, disconnectSocket } from "../socket";
import { useSocketTasks, cancelTask, SocketTask } from "./../hooks/useTasksSocket";
import SessionContext from "../Context/SessionContext";
import { ICliente, IProduto, IProdutoNaoElegivel } from "../interfaces/IGeradora";
import { IControlPageDatagrid, IFiltro, IReqExcel, IReqProduto } from "../interfaces/IGlobal";
import { mask_cgc } from "../utils/format";
import { validateToken } from "../utils/Cookie";

// Componentes Reutilizáveis
import FiltrosGeracao from "../components/FiltrosGeracao";
import TableItens from "../components/TableItens";
import ModalExportExcel from "../components/ModalExportExcel";
import ModalGerarModelo from "../components/ModalGerarModelo";

export default function MainCTA() {
  const { session } = useContext(SessionContext);
  const groups = session.groups;
  const azureId = localStorage.getItem("azureId") || '';
  const navigate = useNavigate();

  // Estados de Controle
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [messageProgress, setMessageProgress] = useState('');
  const [typeModal, setTypeModal] = useState<'export' | 'busca'>('busca');
  const [openCTA, setOpenCTA] = useState(false);

  // Estados de Dados
  const [itensGeradora, setItensGeradora] = useState<IProduto[]>([]);
  const [produtosNaoElegiveis, setProdutosNaoElegiveis] = useState<IProdutoNaoElegivel[]>([]);
  const [discount, setDiscount] = useState('');
  const [codeProduct, setCodeProduct] = useState('');
  const [clickNewVersion, setClickNewVersion] = useState(false);
  const [currentTaskName, setCurrentTaskName] = useState<'busca_produto' | 'gerar_excel'>('busca_produto');

  // Estados de Cliente
  const [busca_por, set_busca_por] = useState<any>({ value: 'todos', description: 'Código/Cnpj/Nome' });
  const [clientList, setClientList] = useState<ICliente[]>([]);
  const [inputClient, setInputClient] = useState('');
  const [client, setClient] = useState<ICliente>();
  const [loadingFindClient, setLoadingFindClient] = useState(false);

  // Paginação
  const [controlPage, setControlPage] = useState<IControlPageDatagrid>({ page: 0, limit: 25 });
  const [rowCount, setRowCount] = useState(0);

  // Snackbar
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [typeMessage, setTypeMessage] = useState<'info' | 'error' | 'warning'>('info');

  const [filtro, setFiltro] = useState<IFiltro>({
    promocao: [], onHold: [], categoria: [], material: [], marca: [], estoque: '=', qtdEstoque: '', regimeTributario: 3,
  });

  // --- Lógica de Socket ---
  const handleCancelar = useCallback((origin: 'export' | 'busca') => {
    const jobCancel = origin === 'busca' ? 'cancelar_busca_produto' : 'cancelar_geracao_excel';
    cancelTask(socket, azureId, jobCancel);
  }, [azureId]);

  const tasks: SocketTask[] = useMemo(() => [{
    id: azureId,
    name: currentTaskName,
    events: {
      exportando: (data: any) => setMessageProgress(data.message),
      consultando: (data: any) => setMessageProgress(data.message),
      finish_busca_produto: (data: any) => {
        setIsLoading(false);
        if (data.status === 200) {
          setItensGeradora(data?.produtos?.map((item: any, index: number) => ({ ...item, id: index + 1 })) || []);
          setProdutosNaoElegiveis(data?.produtosNaoElegiveis || []);
        } else {
          setTypeMessage('error'); setSnackbarMessage(data.message); setOpenSnackbar(true);
        }
        setModalOpen(false);
      },
      finish_gerar_excel: (data: any) => {
        if (data.status === 500) {
          setTypeMessage('error');
          setSnackbarMessage(data.message);
          setOpenSnackbar(true);
        } else {
          if (data.url) {
            const link = document.createElement("a");
            link.href = data.url;
            link.target = "_blank";
            link.click();
          } else if (data.excelBlob) {
            const blob = new Blob([new Uint8Array(data.excelBlob)], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = "Planilha_CTA.xlsx";
            link.click();
          }
        }
        setModalOpen(false);
      }
    },
    cancelEventName: currentTaskName === 'busca_produto' ? "cancelar_busca_produto" : "cancelar_geracao_excel",
  }], [azureId, currentTaskName]);

  useSocketTasks(socket, tasks, () => { });

  // --- Ação de Buscar (Focado em CTA) ---
  const handleGerarCTA = (opt: string) => {
    setOpenCTA(false);
    setIsLoading(true);
    setClickNewVersion(opt === 'btnGerarOnline');
    setCurrentTaskName('busca_produto');
    setTypeModal('busca');
    setModalOpen(true);

    socket.emit("busca_produto", {
      azureId, modelo: 'cta', codEmp: Number(localStorage.getItem("empresa")),
      codPro: codeProduct.split(/[\s,]+/).filter(i => i),
      descontoDigitado: String(discount || '0'),
      versao: opt, filtro, page: controlPage.page + 1, limit: controlPage.limit,
    });

    setIsLoading(false);
  };

  const btnExcel_OnClick = () => {
    const payload: IReqExcel = {
      produtosNaoElegiveis, itens: itensGeradora, sigUfs: filtro.sigUfs || client?.sigUfs || '',
      versao: clickNewVersion ? "btnGerarOnline" : "btnGerarLocal",
      azureId, modelo: 'cta',
      userHasPermission: groups?.filter(g => g.displayName.includes('Espelho')) || []
    };
    const compressed = pako.deflate(JSON.stringify(payload));
    setCurrentTaskName('gerar_excel');
    socket.emit("gerar_excel", compressed);
    setTypeModal('export'); setModalOpen(true);
  };

  // --- Busca de Cliente ---
  const handleSearchClient = useCallback(debounce(async (value: string) => {
    if (!value || value.length < 2) return;
    try {
      setLoadingFindClient(true);
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND}/clients`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        params: { cod_emp: Number(localStorage.getItem("empresa")), search: value, find_by: busca_por?.value }
      });
      setClientList(Array.isArray(data) ? data : data.clients ?? []);
    } finally { setLoadingFindClient(false); }
  }, 500), [busca_por]);

  useEffect(() => { handleSearchClient(inputClient); }, [inputClient, handleSearchClient]);

  useEffect(() => {
    if (!validateToken()) navigate('/login?logout=true');
    connectSocket();
    return () => disconnectSocket();
  }, [navigate]);

  return (
    <Container maxWidth="xl">
      <FiltrosGeracao
        filtro={filtro} setFiltro={setFiltro}
        busca_por={busca_por} set_busca_por={set_busca_por}
        clientList={clientList} client={client}
        inputClient={inputClient} setInputClient={setInputClient}
        handleChangeClient={(cli) => { setClient(cli); setFiltro(p => ({ ...p, sigUfs: cli?.sigUfs, cgcCpfCliente: cli?.cgcCpf })); }}
        loadingFindClient={loadingFindClient} mask_cgc={mask_cgc}
        handleDisabledFieldClick={(msg) => { setTypeMessage('error'); setSnackbarMessage(msg); setOpenSnackbar(true); }}
        discount={discount} setDiscount={setDiscount}
        codeProduct={codeProduct} setCodeProduct={setCodeProduct}
        onEnterCodeProduct={() => setOpenCTA(true)}
      />

      <div className="buttonsDiv">
        <Button
          style={{ textTransform: 'none', backgroundColor: '#1A3661' }}
          variant="contained"
          onClick={() => setOpenCTA(true)}
        >
          Gerar Modelo CTA
        </Button>

        <ModalGerarModelo
          open={openCTA}
          onClose={() => setOpenCTA(false)}
          titulo="Gerar Modelo CTA"
          onConfirm={handleGerarCTA}
        />

        {itensGeradora.length > 0 && (
          <Button
            style={{ textTransform: 'none', backgroundColor: '#1A3661' }}
            variant="contained"
            onClick={btnExcel_OnClick}
          >
            Gerar Excel CTA
          </Button>
        )}

        <ModalExportExcel open={modalOpen} messageExport={messageProgress} handleCancelar={handleCancelar} origin={typeModal} />
      </div>

      <TableItens
        itens={itensGeradora} loading={isLoading}
        cliente={client as ICliente} sigUfs={filtro.sigUfs} groups={groups}
        controlPage={controlPage} setControlPage={setControlPage} tipoGrid="cta"
      />

      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={() => setOpenSnackbar(false)}>
        <Alert severity={typeMessage}>{snackbarMessage}</Alert>
      </Snackbar>
    </Container>
  );
}
